import { useTranslation } from "react-i18next"

import type { HostedAgentArtifactCandidate } from "@/api/client/hosted-agents-artifacts"

import { BookmarkPlus } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

import { ARTIFACT_KIND_LABEL_KEYS } from "../utils/agent-artifacts-utils"

type ArtifactCandidateCardProps = {
  candidate: HostedAgentArtifactCandidate
  agentName: string
  onAccept: () => void
  onDismiss: () => void
}

/**
 * Inline "publish this?" card rendered under an assistant reply whose run
 * context carries an `artifact_candidate` (the agent called `propose_artifact`).
 * Yes opens the publish dialog pre-filled from the candidate; No hides the
 * card for this turn only — nothing is persisted either way.
 */
export function ArtifactCandidateCard({
  candidate,
  agentName,
  onAccept,
  onDismiss,
}: ArtifactCandidateCardProps) {
  const { t } = useTranslation()

  return (
    <div
      aria-label={t("agents.artifacts.candidate_title", { title: candidate.title })}
      className="bg-card flex flex-col gap-3 rounded-lg border p-4 shadow-sm sm:ml-11 sm:flex-row sm:items-center"
      data-testid="artifact-candidate-card"
      role="region"
    >
      <div className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-lg">
        <BookmarkPlus aria-hidden="true" className="text-muted-foreground size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          <span className="line-clamp-2">
            {t("agents.artifacts.candidate_title", { title: candidate.title })}
          </span>
          <Badge className="shrink-0" variant="secondary">
            {t(ARTIFACT_KIND_LABEL_KEYS[candidate.kind])}
          </Badge>
        </div>
        <p className="text-muted-foreground text-xs">
          {t("agents.artifacts.candidate_description", { name: agentName })}
        </p>
        {candidate.summary ? (
          <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">{candidate.summary}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          {t("agents.artifacts.candidate_no")}
        </Button>
        <Button size="sm" onClick={onAccept}>
          <BookmarkPlus aria-hidden="true" className="size-4" />
          {t("agents.artifacts.candidate_yes")}
        </Button>
      </div>
    </div>
  )
}
